import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Button } from '@progress/kendo-react-buttons'
import { FaFileExcel, FaSearch } from 'react-icons/fa'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { useToast } from '../../contexts/ToastContext'
import './EmployeeTimeReport.css'

interface EmployeeTimeRow {
  timeSheetEntryId: number
  timeSheetId: number
  employeeId: number
  employeeName: string
  date: string
  jobId: number
  jobRef: number
  jobRefFormatted: string
  jobDescription: string
  clientName: string
  siteAddress: string
  timeSpentMinutes: number
  timeSpentFormatted: string
  employeeDepartment: string
  jobDepartment: string
  notes: string | null
}

function EmployeeTimeReport() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), 0, 1))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [data, setData] = useState<EmployeeTimeRow[]>([])
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)

  const [sort, setSort] = useState<any[]>([])
  const [filter, setFilter] = useState<any>(null)
  const [skip, setSkip] = useState(0)
  const [pageSize] = useState(25)

  const apiURL = process.env.REACT_APP_API_URL

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          const permitted = currentUser.permissions.some(
            (p) => p.area === 'Reports' && p.permission === 'EmployeeTime' && p.assigned
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('Please select both start and end dates', 'info')
      return
    }
    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error')
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('Authorization token not found')

      const startStr = startDate.toISOString().split('T')[0]
      const endStr = endDate.toISOString().split('T')[0]
      const url = `https://${apiURL}/TimeSheet/report?startDate=${startStr}&endDate=${endStr}`

      const response = await axios.get<EmployeeTimeRow[]>(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      })
      setData(response.data)
      showToast(`Loaded ${response.data.length} entries`, 'success')
    } catch (error: any) {
      console.error('Error fetching employee time data:', error)
      const msg = error.response?.data?.message || error.message || 'Error loading timesheet data'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, showToast, apiURL])

  const dataFiltered = filter ? filterBy(data, filter) : data
  const dataSorted = orderBy(dataFiltered, sort)

  const totalMinutes = dataSorted.reduce((sum, row) => sum + row.timeSpentMinutes, 0)
  const totalDecimalHours = (totalMinutes / 60).toFixed(2).replace(/\.?0+$/, '') || '0'

  const exportToExcel = () => {
    const exportData = dataSorted.map((row) => ({
      'Employee': row.employeeName,
      'Employee Group': row.employeeDepartment,
      'Date': new Date(row.date).toLocaleDateString('en-GB'),
      'Job Ref': row.jobRefFormatted,
      'Job Description': row.jobDescription,
      'Job Type of Work': row.jobDepartment,
      'Client': row.clientName,
      'Time Spent (hrs)': row.timeSpentFormatted,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Time')
    const fileName = `employee_time_report_${startDate?.toISOString().split('T')[0]}_to_${endDate?.toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showToast(`Exported ${exportData.length} rows to ${fileName}`, 'success')
  }

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="employee-time-report__header">
          <h1>Employee Time Report</h1>
        </div>

        {/* Filters */}
        <div style={{
          background: '#f5f9fb',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '1rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            alignItems: 'end',
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                Start Date:
              </label>
              <DatePicker
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                End Date:
              </label>
              <DatePicker
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Button
                onClick={fetchData}
                themeColor="primary"
                disabled={loading || !hasPermission}
                style={{ width: '100%', height: '40px' }}
              >
                <FaSearch style={{ marginRight: '0.5rem' }} />
                {loading ? 'Loading...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary + Export */}
        {data.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button onClick={exportToExcel} themeColor="primary" data-excel="true">
                <FaFileExcel style={{ marginRight: '0.5rem' }} />
                Export to Excel
              </Button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{
                background: '#1e2a4e', color: 'white', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Entries</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{dataSorted.length}</div>
              </div>
              <div style={{
                background: '#aec741', color: '#475315', padding: '1rem',
                borderRadius: '8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Time</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{totalDecimalHours}h</div>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        {data.length > 0 && (
          <div className="employee-time-report-grid">
            <Grid
              data={dataSorted.slice(skip, skip + pageSize)}
              sortable
              sort={sort}
              onSortChange={(e) => setSort(e.sort)}
              filterable
              filter={filter}
              onFilterChange={(e) => {
                setFilter(e.filter)
                setSkip(0)
              }}
              pageable
              pageSize={pageSize}
              skip={skip}
              onPageChange={(e) => setSkip(e.page.skip)}
              total={dataFiltered.length}
              style={{ height: '600px', width: '100%' }}
            >
              <GridColumn field="employeeName" title="Employee" width="160px" />
              <GridColumn field="employeeDepartment" title="Employee Group" width="150px" />
              <GridColumn
                field="date"
                title="Date"
                width="120px"
                cell={(props) => (
                  <td>{new Date(props.dataItem.date).toLocaleDateString('en-GB')}</td>
                )}
              />
              <GridColumn
                field="jobRefFormatted"
                title="Job Ref"
                width="120px"
                cell={(props) => (
                  <td>
                    <span
                      style={{ color: '#1e2a4e', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => navigate(`/jobs?id=${props.dataItem.jobId}`)}
                    >
                      {props.dataItem.jobRefFormatted}
                    </span>
                  </td>
                )}
              />
              <GridColumn field="jobDescription" title="Description" width="200px" />
              <GridColumn field="jobDepartment" title="Job Type of Work" width="150px" />
              <GridColumn field="clientName" title="Client" width="180px" />
              <GridColumn
                field="timeSpentFormatted"
                title="Time Spent (hrs)"
                width="130px"
                cell={(props) => (
                  <td style={{ fontWeight: 'bold' }}>{props.dataItem.timeSpentFormatted}</td>
                )}
              />
            </Grid>
          </div>
        )}

        {/* Empty state */}
        {!loading && data.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#666' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>No timesheet entries found</p>
            <p>Select a date range and click "Run Report" to view employee time data</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmployeeTimeReport
